import { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { IRedux } from '@/_store/types';
import discountService from '@/_service/discount.service';
import { useQuery } from '@tanstack/react-query';
import { IDiscount } from '@/_types';
import { useTelegram } from '@/_providers/telegram';
import { INewDiscountParams } from '@/_service/types';
import dayjs from 'dayjs';
import { useActiveBrand } from '@/_hooks/useActiveBrand';

export default function useDiscount(
  enabled: boolean,
  source_id?: string
): {
  productCount: { ids: Record<string, number>; amount: number };
  discounts: IDiscount[];
} {
  const [discounts, setDiscounts] = useState<IDiscount[]>([]);

  const { user, cartState, common, checkout, session, points } = useSelector(
    (state: IRedux) => ({
      user: state.auth.user,
      cartState: state.cart,
      common: state.common,
      checkout: state.checkout,
      session: state.session,
      points: state.common?.points,
    }),
    shallowEqual
  );
  const commonState = common;
  const checkoutState = checkout;
  const { order_type } = session;

  const { tg_chat_id } = useTelegram();
  const shipperId = useActiveBrand()

  const isBot = Boolean(tg_chat_id);
  const branch = commonState?.branches?.[shipperId]

  const cart = useMemo(() => {
    if (
      !cartState?.cart ||
      typeof cartState.cart !== 'object' ||
      Array.isArray(cartState.cart)
    ) {
      return [];
    }
    return Object.values(cartState.cart).flatMap(
      (brandCart: any) => brandCart?.products || []
    );
  }, [cartState?.cart]);

  const productCount = useMemo(() => {
    let totalQty = 0;
    let myObj: Record<string, number> = {};
    for (const product of cart) {
      myObj[product?.product_id] = product?.quantity;
      totalQty += product?.quantity;
    }
    return {
      ids: myObj,
      amount: (myObj && Object.keys(myObj).length + totalQty) || 0,
    };
  }, [cart]);

  const totalPrice = useMemo(() => {
    if (cart.length === 0) return 0;
    let total = 0;
    cart.forEach((product) => {
      total += (product.price_with_discount || 0) * product.quantity;
      if (product?.order_modifiers?.length > 0) {
        for (const modifier of product.order_modifiers) {
          total +=
            +modifier.modifier_price *
            +(modifier?.modifier_quantity * product.quantity);
        }
      }
    });
    return total;
  }, [cart]);

  const activePoint = useMemo(
    () => points?.find((point) => point.isActive),
    [points]
  );

  const discountParams: INewDiscountParams = useMemo(
    () => ({
      discount_for: 'product,order',
      client_id: user?.id || '',
      order_type_id: order_type?.id,
      delivery_price: 0,
      branch_ids: branch?.id,
      with_cashback: false,
      order_source_id:
        source_id ||
        (isBot
          ? '02609982-bd15-41e5-ba06-2cdcb356d5fb'
          : '3429e492-5faa-4e17-a19e-b32a4d01b267'),
      payment_type_id: checkoutState?.payment_type?.id || '',
      product_ids_counts: JSON.stringify(productCount?.ids),
      usable_with_others: true,
      for_order_amount: totalPrice,
      lat: activePoint?.location?.[0],
      long: activePoint?.location?.[1],
      future_time: checkoutState?.future_time
        ? dayjs(checkoutState.future_time).format('YYYY-MM-DD HH:mm:ss')
        : undefined,
    }),
    [
      user?.id,
      order_type?.id,
      branch?.id,
      source_id,
      isBot,
      checkoutState?.payment_type?.id,
      productCount?.ids,
      totalPrice,
      activePoint?.location,
      checkoutState?.future_time,
    ]
  );

  // const debouncedParams = useDebounce(discountParams, 300)

  // Get order discount
  const { data: allDiscounts, isSuccess } = useQuery({
    queryKey: ['ALL_DISCOUNTS', discountParams, enabled, shipperId],
    queryFn: () => discountService.all(discountParams, shipperId),
    enabled: Boolean(totalPrice && order_type?.id && enabled),
    retry: false,
  });

  useEffect(() => {
    if (isSuccess) {
      const filtered = allDiscounts?.data?.discounts?.reduce(
        (acc: { product: IDiscount[]; order: IDiscount[] }, cur) => {
          acc[cur.discount_for] = [...acc[cur.discount_for], cur];

          return acc;
        },
        { product: [], order: [] }
      );

      setDiscounts(filtered?.order || []);
    }
  }, [allDiscounts, isSuccess]);

  return { productCount, discounts };
}
